import React from 'react';
import { Palette, Sparkles, Heart, Clock } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: Palette,
      title: 'Hand-Painted Artistry',
      description: 'Every strand is carefully painted by our expert colorists for a bespoke, natural result',
    },
    {
      icon: Sparkles,
      title: 'Sun-Kissed Dimension',
      description: 'Creates effortless, luminous tones that mimic what the sun naturally does to your hair',
    },
    {
      icon: Heart,
      title: 'Low Maintenance',
      description: 'Beautiful grow-out that looks intentional, with touch-ups needed only every 10-16 weeks',
    },
    {
      icon: Clock,
      title: 'Expert Care',
      description: 'Luxury experience with professional consultation, custom toning, and signature blowout',
    },
  ];

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-black via-[#0f0f0f] to-black relative overflow-hidden">
      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[100px]"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-[#d4af37] text-sm font-semibold tracking-[0.2em] uppercase">About Balayage</span>
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-[#f5f5f0] mb-6">
            Vancouver's Guide to
            <span className="block text-[#d4af37]">Balayage Hair Colour</span>
          </h2>
          <p className="text-[#f5f5f0]/70 text-lg max-w-3xl mx-auto leading-relaxed">
            Balayage in Vancouver has become one of the most requested hair colour services in the city — from Downtown Vancouver and the West End to Yaletown, Davie Village, Kitsilano, and Coal Harbour. It's low maintenance, grows out beautifully, and creates that effortless, sun-kissed look without the commitment of all-over colour. At Prép Beauty Parlour, our Vancouver balayage specialists paint every strand by hand for a bespoke result.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-8 bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-all duration-500 hover:shadow-[0_0_30px_rgba(212,175,55,0.15)] hover:-translate-y-2"
            >
              <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 flex items-center justify-center mb-6 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                <feature.icon className="text-[#d4af37]" size={28} />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#f5f5f0] mb-3">{feature.title}</h3>
              <p className="text-[#f5f5f0]/60 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/30 p-12 backdrop-blur-sm">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-['Cormorant_Garamond'] text-4xl text-[#f5f5f0] mb-6">
                What Is Balayage?
              </h3>
              <p className="text-[#f5f5f0]/70 leading-relaxed mb-6">
                Balayage (pronounced <em>bah-lee-ahje</em>) is a French hair colouring technique where colour is hand-painted directly onto the hair — no foils, no uniform sections. The result is a soft gradient of light that mimics what the sun naturally does to your hair, making it the most-searched balayage service in Vancouver.
              </p>
              <p className="text-[#f5f5f0]/70 leading-relaxed mb-6">
                Unlike traditional highlights, balayage creates softer, more blended results with a natural grow-out. Most Vancouver clients — from Downtown to Kitsilano and Yaletown — can go 3–4 months between appointments, making it one of the most low-maintenance colour services available in the city.
              </p>
              <div className="space-y-3 pt-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#d4af37] mt-2"></div>
                  <p className="text-[#f5f5f0]/80"><strong className="text-[#d4af37]">Hand-Painted</strong> — Applied freehand for a natural, custom result</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#d4af37] mt-2"></div>
                  <p className="text-[#f5f5f0]/80"><strong className="text-[#d4af37]">No Harsh Lines</strong> — Colour fades seamlessly into your natural base</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#d4af37] mt-2"></div>
                  <p className="text-[#f5f5f0]/80"><strong className="text-[#d4af37]">Low Maintenance</strong> — Grow-out looks intentional, not neglected</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#d4af37] mt-2"></div>
                  <p className="text-[#f5f5f0]/80"><strong className="text-[#d4af37]">Versatile</strong> — Works on all hair types, textures, and base colours</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#c9a961]/10 blur-3xl"></div>
              <video
                data-testid="about-video"
                src="https://customer-assets.emergentagent.com/job_luxury-locks-4/artifacts/nundbw5r_Bailee-top-hiar-salon-balayage.mov"
                className="relative w-full h-[500px] object-cover shadow-2xl border border-[#c9a961]/20"
                autoPlay
                muted
                loop
                playsInline
                aria-label="Bailee demonstrating balayage hair coloring process at Prép Beauty Parlour salon in Vancouver"
                title="Professional Balayage Technique by Bailee - Top Hair Salon Vancouver"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
